import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Checkbox,
} from '@mui/material';
import { TrendingUpIcon, UploadIcon, FileIcon, DeleteIcon } from 'lucide-react';
import { useAuthHeader } from '@/hooks/useAuth';
import {
  initUploadV2,
  startAnalysisV2,
  listJobsV2,
  getLatestExecutionDefaultsV2,
} from '@/lib/api/logistics-shipping-kpi-v2';
import { getProductGroupTypes, type ProductGroupType } from '@/lib/api/product-group-type';
import type { SimpleAccount } from '@/lib/types';
import type {
  LogisticsShippingKpiJobV2,
  StartAnalysisRequestV2,
} from '@/lib/types/feature/logistics-shipping-kpi-v2';
import type { ExecutionFiles } from '@/lib/types/feature/logistics-shipping-kpi';
import { FileUploadCard } from './FileUploadCard';

interface LogisticsShippingKpiV2Props {
  account: SimpleAccount;
}

/**
 * JST対応の日付計算ヘルパー関数
 */
const calculateDefaultDates = (baseDate: string) => {
  // 基準日をJSTで解釈
  const baseDateObj = new Date(baseDate + 'T00:00:00+09:00');

  // 集計期間: 終了日=基準日、開始日=基準日の6日前（7日間）
  const aggregationEnd = new Date(baseDateObj);
  const aggregationStart = new Date(baseDateObj);
  aggregationStart.setDate(aggregationStart.getDate() - 6);

  // 比較期間: 1年前の同曜日に調整
  const comparisonEnd = new Date(baseDateObj);
  comparisonEnd.setFullYear(comparisonEnd.getFullYear() - 1);

  const comparisonStart = new Date(aggregationStart);
  comparisonStart.setFullYear(comparisonStart.getFullYear() - 1);

  // 同曜日調整: 集計期間開始日と比較期間開始日の曜日を合わせる
  const aggregationStartDay = aggregationStart.getDay();
  const comparisonStartDay = comparisonStart.getDay();
  const dayDiff = aggregationStartDay - comparisonStartDay;

  // 比較期間を調整（最大±3日以内で同曜日に合わせる）
  comparisonStart.setDate(comparisonStart.getDate() + dayDiff);
  comparisonEnd.setDate(comparisonEnd.getDate() + dayDiff);

  // YYYY-MM-DD形式に変換（JSTタイムゾーンで）
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('sv-SE'); // sv-SEロケールはYYYY-MM-DD形式
  };

  return {
    startDate: formatDate(aggregationStart),
    endDate: formatDate(aggregationEnd),
    comparisonStartDate: formatDate(comparisonStart),
    comparisonEndDate: formatDate(comparisonEnd),
  };
};

export function LogisticsShippingKpiV2({ account }: LogisticsShippingKpiV2Props) {
  const { getAuthHeader } = useAuthHeader();

  // 日付状態（初期値は空欄）
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [comparisonStartDate, setComparisonStartDate] = useState('');
  const [comparisonEndDate, setComparisonEndDate] = useState('');

  // 新商品判定基準日（任意）
  const [newProductBaseDate, setNewProductBaseDate] = useState<string>('');

  // 展開店舗フィルタの適用（デフォルト：true）
  const [applyDealingSkuFilter, setApplyDealingSkuFilter] = useState<boolean>(true);

  // 展開判定パラメータ（チェックON時のみ使用）
  const [recentStockDay, setRecentStockDay] = useState<number | undefined>(undefined);
  const [requiredStockQuantity, setRequiredStockQuantity] = useState<number | undefined>(undefined);
  const [stockDayThreshold, setStockDayThreshold] = useState<number | undefined>(undefined);
  const [recentSalesDay, setRecentSalesDay] = useState<number | undefined>(undefined);
  const [requiredSalesQuantity, setRequiredSalesQuantity] = useState<number | undefined>(undefined);
  const [salesDayThreshold, setSalesDayThreshold] = useState<number | undefined>(undefined);

  // ファイル状態
  const [shippingOrders, setShippingOrders] = useState<File | null>(null);
  const [targetSellers, setTargetSellers] = useState<File | null>(null);
  const [sourceWarehouseMapping, setSourceWarehouseMapping] = useState<File | null>(null);
  const [excludedSkus, setExcludedSkus] = useState<File | null>(null);
  const [excludedSellerSkuCombinations, setExcludedSellerSkuCombinations] = useState<File | null>(
    null,
  );
  const [productGroupFilter, setProductGroupFilter] = useState<File | null>(null);
  const [newProductExclusion, setNewProductExclusion] = useState<File | null>(null);
  const [newProductExclusionType, setNewProductExclusionType] = useState<'store_group' | 'store'>(
    'store_group',
  );

  // UI状態
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // 前回実行の情報を保持するstate
  const [previousExecution, setPreviousExecution] = useState<ExecutionFiles | null>(null);
  const [previousExecutionDate, setPreviousExecutionDate] = useState<string | undefined>(undefined);

  // 個別ファイルの前回使用フラグ
  const [usePreviousFiles, setUsePreviousFiles] = useState({
    targetSellers: false,
    shippingOrders: false,
    sourceWarehouseMapping: false,
    excludedSkus: false,
    excludedSellerSkuCombinations: false,
    productGroupFilter: false,
    newProductExclusion: false,
  });

  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);

  // 初期化済みフラグ（一度だけ初期値を設定するため）
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastInitializedAccountId, setLastInitializedAccountId] = useState<string | null>(null);

  // 商品グループタイプ（UI表示・選択用）
  const [productGroupTypes, setProductGroupTypes] = useState<ProductGroupType[]>([]);
  const [selectedProductGroupTypeIds, setSelectedProductGroupTypeIds] = useState<string[]>([]);
  // 商品グループタイプごとの階層指定（タイプID -> 階層）
  const [productGroupSummaryDepths, setProductGroupSummaryDepths] = useState<Map<string, number>>(
    new Map(),
  );
  // 初期復元用に、一時的に保持する前回選択ID（タイプ一覧読み込み待ち）
  const [pendingSelectedTypeIds, setPendingSelectedTypeIds] = useState<string[] | null>(null);
  const [isLoadingProductGroupTypes, setIsLoadingProductGroupTypes] = useState(false);
  // 上位品割合（%）UI必須
  const [topProductThresholdPercent, setTopProductThresholdPercent] = useState<string>('');
  // 非稼働判定窓（日数）UI必須。既定値は30日
  const [inactiveWindowDays, setInactiveWindowDays] = useState<string>('30');

  // アカウント変更時に初回のみ最新実行のデフォルト値を取得（v2用）
  useEffect(() => {
    const loadDefaults = async () => {
      if (!account.id) return;

      // 既に初期化済みで、同じアカウントの場合はスキップ
      if (isInitialized && lastInitializedAccountId === account.id) {
        return;
      }

      setIsLoadingDefaults(true);
      setError(null);

      try {
        const authHeaders = getAuthHeader();
        const defaults = await getLatestExecutionDefaultsV2(account.id, authHeaders);

        if (defaults.parameters) {
          // パラメータをstateに設定
          setStartDate(defaults.parameters.start_date);
          setEndDate(defaults.parameters.end_date);
          setComparisonStartDate(defaults.parameters.comparison_start_date);
          setComparisonEndDate(defaults.parameters.comparison_end_date);
          setNewProductBaseDate(defaults.parameters.new_product_base_date ?? '');
          setApplyDealingSkuFilter(defaults.parameters.apply_dealing_sku_filter);
          setNewProductExclusionType(
            (defaults.parameters.new_product_exclusion_type as 'store_group' | 'store') ??
              'store_group',
          );
          // 展開判定パラメータ（存在すれば初期化）
          setRecentStockDay(defaults.parameters.recent_stock_day ?? undefined);
          setRequiredStockQuantity(defaults.parameters.required_stock_quantity ?? undefined);
          setStockDayThreshold(defaults.parameters.stock_day_threshold ?? undefined);
          setRecentSalesDay(defaults.parameters.recent_sales_day ?? undefined);
          setRequiredSalesQuantity(defaults.parameters.required_sales_quantity ?? undefined);
          setSalesDayThreshold(defaults.parameters.sales_day_threshold ?? undefined);
          // 上位品割合の設定（null/undefined の場合は空文字）
          if (typeof defaults.parameters?.top_product_threshold_percent === 'number') {
            setTopProductThresholdPercent(
              defaults.parameters.top_product_threshold_percent.toString(),
            );
          } else {
            // 前回実行に上位品割合の設定がない場合は空文字（必須項目なのでユーザーが入力）
            setTopProductThresholdPercent('');
          }
          // 非稼働判定窓（日数）の設定（前回値があれば復元、なければ既定値30）
          if (typeof defaults.parameters?.inactive_window_days === 'number') {
            setInactiveWindowDays(defaults.parameters.inactive_window_days.toString());
          } else {
            setInactiveWindowDays('30');
          }

          // 前回ファイル情報を保存
          if (defaults.files) {
            setPreviousExecution(defaults.files);

            // 前回ファイル使用フラグを設定（前回ファイルが存在するもののみ）
            setUsePreviousFiles({
              targetSellers: !!defaults.files.target_sellers_file_name,
              shippingOrders: !!defaults.files.shipping_orders_file_name,
              sourceWarehouseMapping: !!defaults.files.store_warehouse_mapping_file_name,
              excludedSkus: !!defaults.files.excluded_skus_file_name,
              excludedSellerSkuCombinations:
                !!defaults.files.excluded_seller_sku_combinations_file_name,
              productGroupFilter: !!defaults.files.product_group_filter_file_name,
              newProductExclusion: !!defaults.files.new_product_exclusion_file_name,
            });
          }

          // 実行日時を保存
          if (defaults.execution_info) {
            setPreviousExecutionDate(defaults.execution_info.execution_date);
          }

          // 商品グループタイプの初期選択復元
          if (defaults.parameters?.selected_product_group_type_ids) {
            // タイプ一覧のロード完了後に適用するため一時保存
            setPendingSelectedTypeIds(
              defaults.parameters.selected_product_group_type_ids.slice(0, 15),
            );
          } else {
            setPendingSelectedTypeIds([]);
          }

          // 商品グループサマリー階層の復元
          if (defaults.parameters?.product_group_summary_depths) {
            const depthsMap = new Map<string, number>();
            Object.entries(defaults.parameters.product_group_summary_depths).forEach(
              ([typeId, depth]) => {
                depthsMap.set(typeId, Math.min(5, Math.max(1, depth as number)));
              },
            );
            setProductGroupSummaryDepths(depthsMap);
          } else if (defaults.parameters?.product_group_summary_depth) {
            // 後方互換性: 単一値の場合は空のMap（選択時にデフォルト1階層が設定される）
            setProductGroupSummaryDepths(new Map());
          } else {
            setProductGroupSummaryDepths(new Map());
          }

          setSuccess('前回の実行設定を読み込みました');
        } else {
          // 従来のデフォルト値を設定
          setDefaultValues();
        }
      } catch (error) {
        setError('前回実行の取得に失敗しました。デフォルト値を使用します。');
        setDefaultValues();
      } finally {
        setIsLoadingDefaults(false);
        setIsInitialized(true);
        setLastInitializedAccountId(account.id);
      }
    };

    const setDefaultValues = () => {
      if (account.base_date) {
        try {
          const defaultDates = calculateDefaultDates(account.base_date);
          setStartDate(defaultDates.startDate);
          setEndDate(defaultDates.endDate);
          setComparisonStartDate(defaultDates.comparisonStartDate);
          setComparisonEndDate(defaultDates.comparisonEndDate);
          setNewProductBaseDate(account.base_date);
        } catch (error) {
          // エラーが発生した場合は基準日のみ設定
          setNewProductBaseDate(account.base_date);
        }
        // 上位品割合は前回実行がない場合は空文字（必須項目なのでユーザーが入力）
        setTopProductThresholdPercent('');
        // 非稼働判定窓（日数）は前回実行がない場合は既定値30
        setInactiveWindowDays('30');
      }
    };

    loadDefaults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.id]);

  // 商品グループタイプ一覧を取得（アカウント変更時）
  useEffect(() => {
    const loadTypes = async () => {
      if (!account.id) return;
      try {
        setIsLoadingProductGroupTypes(true);
        const auth = getAuthHeader();
        const types = await getProductGroupTypes(account.id, auth);
        // sort_order順で並べ替え（バックエンドも同順想定だが念のため）
        const sorted = [...types].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        setProductGroupTypes(sorted);
      } catch (e) {
        // サイレント失敗（UIは空のまま）
      } finally {
        setIsLoadingProductGroupTypes(false);
      }
    };
    loadTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.id]);

  // タイプ一覧の取得完了を待って初期選択を適用
  useEffect(() => {
    if (pendingSelectedTypeIds === null) return;
    if (productGroupTypes.length === 0) return;
    // ロード済みタイプに存在するIDのみを適用
    const existingIds = new Set(productGroupTypes.map((t) => t.id));
    const filtered = pendingSelectedTypeIds.filter((id) => existingIds.has(id));
    setSelectedProductGroupTypeIds(filtered);
    // 選択されたタイプで階層が未設定の場合はデフォルト値（1階層）を設定
    setProductGroupSummaryDepths((depths) => {
      const newDepths = new Map(depths);
      filtered.forEach((typeId) => {
        if (!newDepths.has(typeId)) {
          newDepths.set(typeId, 1);
        }
      });
      return newDepths;
    });
    // 一度適用したらクリア
    setPendingSelectedTypeIds(null);
  }, [productGroupTypes, pendingSelectedTypeIds]);

  const handleFileUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
    setFile: React.Dispatch<React.SetStateAction<File | null>>,
    fileType: keyof typeof usePreviousFiles,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setFile(file);
      // 新しいファイルがアップロードされたら、該当するファイルタイプの前回ファイル使用フラグをクリア
      setUsePreviousFiles((prev) => ({
        ...prev,
        [fileType]: false,
      }));
    }
  };

  // 前回ファイル使用/非使用の切り替え
  const togglePreviousFileUsage = (fileType: keyof typeof usePreviousFiles) => {
    setUsePreviousFiles((prev) => ({
      ...prev,
      [fileType]: !prev[fileType],
    }));

    // 前回ファイルを使用する場合は、アップロードされたファイルをクリア
    if (!usePreviousFiles[fileType]) {
      switch (fileType) {
        case 'targetSellers':
          setTargetSellers(null);
          break;
        case 'shippingOrders':
          setShippingOrders(null);
          break;
        case 'sourceWarehouseMapping':
          setSourceWarehouseMapping(null);
          break;
        case 'excludedSkus':
          setExcludedSkus(null);
          break;
        case 'excludedSellerSkuCombinations':
          setExcludedSellerSkuCombinations(null);
          break;
        case 'productGroupFilter':
          setProductGroupFilter(null);
          break;
        case 'newProductExclusion':
          setNewProductExclusion(null);
          break;
      }
    }
  };

  const handleRemoveFile = (setter: (file: File | null) => void) => {
    setter(null);
  };

  const handleAnalyze = async () => {
    setError(null);
    setSuccess(null);

    // バリデーション
    if (!startDate || !endDate) {
      setError('集計期間を入力してください');
      return;
    }

    if (!comparisonStartDate || !comparisonEndDate) {
      setError('比較期間を入力してください');
      return;
    }

    // 集計期間の日付妥当性チェック
    if (startDate > endDate) {
      setError('集計期間の開始日は終了日以前に設定してください');
      return;
    }

    // 比較期間の日付妥当性チェック
    if (comparisonStartDate > comparisonEndDate) {
      setError('比較期間の開始日は終了日以前に設定してください');
      return;
    }

    // 集計期間の最大日数チェック（35日）
    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);
    const daysDiff =
      Math.ceil((endDateObj.getTime() - startDateObj.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    if (daysDiff > 35) {
      setError('集計期間は最大35日間まで設定可能です');
      return;
    }

    // 比較期間の最大日数チェック（35日）
    const comparisonStartDateObj = new Date(comparisonStartDate);
    const comparisonEndDateObj = new Date(comparisonEndDate);
    const comparisonDaysDiff =
      Math.ceil(
        (comparisonEndDateObj.getTime() - comparisonStartDateObj.getTime()) / (1000 * 60 * 60 * 24),
      ) + 1;
    if (comparisonDaysDiff > 35) {
      setError('比較期間は最大35日間まで設定可能です');
      return;
    }

    // 上位品割合（%）のバリデーション（必須、整数、0〜100範囲）
    const rawTop = (topProductThresholdPercent ?? '').toString().trim();
    if (rawTop === '') {
      setError('上位品割合（%）は必須です');
      return;
    }
    if (!/^[-]?\d+$/.test(rawTop)) {
      setError('上位品割合（%）は整数で入力してください（0〜100）');
      return;
    }
    const topPercentValue = parseInt(rawTop, 10);
    if (topPercentValue < 0 || topPercentValue > 100) {
      setError('上位品割合（%）は0〜100の範囲で入力してください');
      return;
    }

    // 非稼働判定窓（日数）のバリデーション（必須、整数、1以上）
    const rawWindow = (inactiveWindowDays ?? '').toString().trim();
    if (rawWindow === '') {
      setError('非稼働判定期間（日数）は必須です');
      return;
    }
    if (!/^\d+$/.test(rawWindow)) {
      setError('非稼働判定期間（日数）は1以上の整数で入力してください');
      return;
    }
    const inactiveWindowValue = parseInt(rawWindow, 10);
    if (inactiveWindowValue < 1) {
      setError('非稼働判定期間（日数）は1以上で入力してください');
      return;
    }

    if (
      (!shippingOrders && !usePreviousFiles.shippingOrders) ||
      (!targetSellers && !usePreviousFiles.targetSellers) ||
      (!sourceWarehouseMapping && !usePreviousFiles.sourceWarehouseMapping)
    ) {
      setError(
        '必須ファイル（出荷指示書、集計対象の店舗、出荷元倉庫マッピング）をアップロードまたは前回実行の設定が必要です',
      );
      return;
    }

    // 新商品除外ファイルと基準日の整合性チェック
    if ((newProductExclusion || usePreviousFiles.newProductExclusion) && !newProductBaseDate) {
      setError('新商品除外ファイルがアップロードされた場合、新商品判定基準日は必須です');
      return;
    }

    // 展開判定パラメータ（チェックON時の必須チェック）
    if (applyDealingSkuFilter) {
      const params = [
        recentStockDay,
        requiredStockQuantity,
        stockDayThreshold,
        recentSalesDay,
        requiredSalesQuantity,
        salesDayThreshold,
      ];
      if (params.some((v) => v === undefined || v === null)) {
        setError('展開店舗フィルタがONの場合、6つの展開判定パラメータはすべて必須です');
        return;
      }
    }

    try {
      setIsAnalyzing(true);

      // 出荷指示書はS3直PUTに切替
      let previousShippingOrdersPath: string | undefined = undefined;
      if (shippingOrders) {
        if (shippingOrders.size > 200 * 1024 * 1024) {
          throw new Error('出荷指示書は200MB以下にしてください');
        }
        const initRes = await initUploadV2(
          account.id,
          { fileName: shippingOrders.name, contentLength: shippingOrders.size },
          getAuthHeader(),
        );
        // PUT
        const putHeaders = {
          'Content-Type': initRes.shipping_orders.requiredHeaders['Content-Type'],
        };
        const putRes = await fetch(initRes.shipping_orders.presignedPutUrl, {
          method: 'PUT',
          headers: putHeaders,
          body: shippingOrders,
        });
        if (!putRes.ok) {
          const text = await putRes.text().catch(() => '');
          throw new Error(`S3アップロードに失敗しました: ${putRes.status} ${text}`);
        }
        // previous path 相当としてキーをセット
        previousShippingOrdersPath = initRes.shipping_orders.s3Key;
      } else if (usePreviousFiles.shippingOrders) {
        // デバッグ: 前回ファイル使用時の状態を確認
        console.log('前回ファイル使用チェック:', {
          usePreviousFiles_shippingOrders: usePreviousFiles.shippingOrders,
          previousExecution: previousExecution ? 'exists' : 'null',
          shipping_orders_file_path: previousExecution?.shipping_orders_file_path,
          shipping_orders_file_name: previousExecution?.shipping_orders_file_name,
        });
        if (
          previousExecution?.shipping_orders_file_path &&
          previousExecution.shipping_orders_file_path.trim() !== ''
        ) {
          previousShippingOrdersPath = previousExecution.shipping_orders_file_path;
        }
      }

      const authHeaders = getAuthHeader();
      // start API に統一
      if (!previousShippingOrdersPath) {
        throw new Error('出荷指示書のアップロードが必要です');
      }
      const initRes = await initUploadV2(
        account.id,
        { fileName: 'noop.csv', contentLength: 0 },
        authHeaders,
      );

      const request: StartAnalysisRequestV2 = {
        startDate,
        endDate,
        comparisonStartDate,
        comparisonEndDate,
        newProductBaseDate: newProductBaseDate || undefined,
        applyDealingSkuFilter,
        recentStockDay: applyDealingSkuFilter ? recentStockDay : undefined,
        requiredStockQuantity: applyDealingSkuFilter ? requiredStockQuantity : undefined,
        stockDayThreshold: applyDealingSkuFilter ? stockDayThreshold : undefined,
        recentSalesDay: applyDealingSkuFilter ? recentSalesDay : undefined,
        requiredSalesQuantity: applyDealingSkuFilter ? requiredSalesQuantity : undefined,
        salesDayThreshold: applyDealingSkuFilter ? salesDayThreshold : undefined,
        topProductThresholdPercent: topPercentValue,
        inactiveWindowDays: inactiveWindowValue,
        product_group_summary_depths:
          selectedProductGroupTypeIds.length > 0
            ? Object.fromEntries(
                selectedProductGroupTypeIds.map((typeId) => [
                  typeId,
                  productGroupSummaryDepths.get(typeId) ?? 1,
                ]),
              )
            : undefined,
        files: {
          shippingOrders: undefined,
          targetSellers: targetSellers || undefined,
          sourceWarehouseMapping: sourceWarehouseMapping || undefined,
          excludedSkus: excludedSkus || undefined,
          excludedSellerSkuCombinations: excludedSellerSkuCombinations || undefined,
          productGroupFilter: productGroupFilter || undefined,
          newProductExclusion: newProductExclusion || undefined,
        },
        previousFiles: {
          targetSellersPath:
            usePreviousFiles.targetSellers && previousExecution?.target_sellers_file_path
              ? previousExecution.target_sellers_file_path
              : undefined,
          excludedSkusPath:
            usePreviousFiles.excludedSkus && previousExecution?.excluded_skus_file_path
              ? previousExecution.excluded_skus_file_path
              : undefined,
          excludedSellerSkuCombinationsPath:
            usePreviousFiles.excludedSellerSkuCombinations &&
            previousExecution?.excluded_seller_sku_combinations_file_path
              ? previousExecution.excluded_seller_sku_combinations_file_path
              : undefined,
          shippingOrdersPath: previousShippingOrdersPath,
          sourceWarehouseMappingPath:
            usePreviousFiles.sourceWarehouseMapping &&
            previousExecution?.store_warehouse_mapping_file_path
              ? previousExecution.store_warehouse_mapping_file_path
              : undefined,
          productGroupFilterPath:
            usePreviousFiles.productGroupFilter && previousExecution?.product_group_filter_file_path
              ? previousExecution.product_group_filter_file_path
              : undefined,
          newProductExclusionPath:
            usePreviousFiles.newProductExclusion &&
            previousExecution?.new_product_exclusion_file_path
              ? previousExecution.new_product_exclusion_file_path
              : undefined,
        },
        newProductExclusionType,
        selectedProductGroupTypeIds:
          selectedProductGroupTypeIds.length > 0
            ? selectedProductGroupTypeIds.slice(0, 15)
            : undefined,
        version: 2,
      };

      const result = await startAnalysisV2(
        account.id,
        initRes.job_id,
        previousShippingOrdersPath,
        request,
        authHeaders,
      );

      setSuccess(`${result.message} ジョブID: ${result.job_id} (ステータス: ${result.job_status})`);
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('分析に失敗しました。入力内容とファイル形式を確認してください。');
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6">集計期間</Typography>
              <Chip label="必須" size="small" variant="outlined" color="error" />
            </Box>
            <Stack direction="row" spacing={2}>
              <TextField
                label="開始日"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="終了日"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6">比較期間</Typography>
              <Chip label="必須" size="small" variant="outlined" color="error" />
            </Box>
            <Stack direction="row" spacing={2}>
              <TextField
                label="比較開始日"
                type="date"
                value={comparisonStartDate}
                onChange={(e) => setComparisonStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="比較終了日"
                type="date"
                value={comparisonEndDate}
                onChange={(e) => setComparisonEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              集計条件
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body1" component="label">
                    上位品割合（%）
                  </Typography>
                  <Chip label="必須" size="small" variant="outlined" color="error" />
                </Box>
                <TextField
                  type="number"
                  value={topProductThresholdPercent}
                  onChange={(e) => {
                    setTopProductThresholdPercent(e.target.value);
                  }}
                  fullWidth
                />
              </Box>
            </Box>

            {/* 非稼働判定期間（日数） */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body1" component="label">
                    非稼働判定期間（日数）
                  </Typography>
                  <Chip label="必須" size="small" variant="outlined" color="error" />
                </Box>
                <TextField
                  type="number"
                  value={inactiveWindowDays}
                  onChange={(e) => {
                    setInactiveWindowDays(e.target.value);
                  }}
                  fullWidth
                  helperText={`集計期間の終了日からさかのぼって ${
                    inactiveWindowDays || '◯'
                  } 日間を、非稼働在庫の判定期間とします（この期間内に販売がないSKUを非稼働と判定）`}
                />
              </Box>
            </Box>

            {/* 展開店舗フィルタの適用 */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body1" component="label">
                    展開店舗フィルタの適用
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={applyDealingSkuFilter}
                        onChange={(e) => setApplyDealingSkuFilter(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="展開店舗の判定を行う"
                  />
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '0.875rem', lineHeight: 1.4 }}
                >
                  展開判定ONの場合、下記の6つの展開判定パラメータすべてが必須になります。
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '0.875rem', lineHeight: 1.4 }}
                >
                  ※ 判定の基準日は集計期間の開始日です
                </Typography>
              </Box>
            </Box>

            {/* 展開判定パラメータ（チェックON時のみ表示） */}
            {applyDealingSkuFilter && (
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    展開判定パラメータ
                  </Typography>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={2}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          在庫
                        </Typography>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            在庫参照直近日数{' '}
                            <Typography component="span" variant="caption" color="text.secondary">
                              (recent_stock_day)
                            </Typography>
                          </Typography>
                          <TextField
                            type="number"
                            value={recentStockDay ?? ''}
                            onChange={(e) =>
                              setRecentStockDay(
                                e.target.value === ''
                                  ? undefined
                                  : parseInt(e.target.value) || undefined,
                              )
                            }
                            inputProps={{ min: 1, step: 1 }}
                            fullWidth
                            required
                          />
                        </Box>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            基準在庫数{' '}
                            <Typography component="span" variant="caption" color="text.secondary">
                              (required_stock_quantity)
                            </Typography>
                          </Typography>
                          <TextField
                            type="number"
                            value={requiredStockQuantity ?? ''}
                            onChange={(e) =>
                              setRequiredStockQuantity(
                                e.target.value === ''
                                  ? undefined
                                  : parseInt(e.target.value) || undefined,
                              )
                            }
                            inputProps={{ min: 0, step: 1 }}
                            fullWidth
                            required
                          />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            在庫日数閾値{' '}
                            <Typography component="span" variant="caption" color="text.secondary">
                              (stock_day_threshold)
                            </Typography>
                          </Typography>
                          <TextField
                            type="number"
                            value={stockDayThreshold ?? ''}
                            onChange={(e) =>
                              setStockDayThreshold(
                                e.target.value === ''
                                  ? undefined
                                  : parseInt(e.target.value) || undefined,
                              )
                            }
                            inputProps={{ min: 0, step: 1 }}
                            fullWidth
                            required
                          />
                        </Box>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          売上
                        </Typography>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            売上参照直近日数{' '}
                            <Typography component="span" variant="caption" color="text.secondary">
                              (recent_sales_day)
                            </Typography>
                          </Typography>
                          <TextField
                            type="number"
                            value={recentSalesDay ?? ''}
                            onChange={(e) =>
                              setRecentSalesDay(
                                e.target.value === ''
                                  ? undefined
                                  : parseInt(e.target.value) || undefined,
                              )
                            }
                            inputProps={{ min: 1, step: 1 }}
                            fullWidth
                            required
                          />
                        </Box>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            基準販売数{' '}
                            <Typography component="span" variant="caption" color="text.secondary">
                              (required_sales_quantity)
                            </Typography>
                          </Typography>
                          <TextField
                            type="number"
                            value={requiredSalesQuantity ?? ''}
                            onChange={(e) =>
                              setRequiredSalesQuantity(
                                e.target.value === ''
                                  ? undefined
                                  : parseInt(e.target.value) || undefined,
                              )
                            }
                            inputProps={{ min: 0, step: 1 }}
                            fullWidth
                            required
                          />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            販売日数閾値{' '}
                            <Typography component="span" variant="caption" color="text.secondary">
                              (sales_day_threshold)
                            </Typography>
                          </Typography>
                          <TextField
                            type="number"
                            value={salesDayThreshold ?? ''}
                            onChange={(e) =>
                              setSalesDayThreshold(
                                e.target.value === ''
                                  ? undefined
                                  : parseInt(e.target.value) || undefined,
                              )
                            }
                            inputProps={{ min: 0, step: 1 }}
                            fullWidth
                            required
                          />
                        </Box>
                      </Box>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      チェックONの場合は6項目すべて必須です
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            )}

            <Divider sx={{ my: 2 }} />

            {/* 商品グループタイプ選択 */}
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1">
                  明細に出力する商品グループタイプ（最大15件）
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  表示に時間がかかる場合があります
                </Typography>
              </Box>
              {isLoadingProductGroupTypes ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="caption" color="text.secondary">
                    商品グループタイプを読み込み中...
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {productGroupTypes.map((t) => {
                    const checked = selectedProductGroupTypeIds.includes(t.id);
                    const disabled = !checked && selectedProductGroupTypeIds.length >= 15;
                    return (
                      <Chip
                        key={t.id}
                        label={t.name}
                        color={checked ? 'primary' : 'default'}
                        variant={checked ? 'filled' : 'outlined'}
                        onClick={() => {
                          setSelectedProductGroupTypeIds((prev) => {
                            if (prev.includes(t.id)) {
                              // 選択解除時は階層設定も削除
                              setProductGroupSummaryDepths((depths) => {
                                const newDepths = new Map(depths);
                                newDepths.delete(t.id);
                                return newDepths;
                              });
                              return prev.filter((id) => id !== t.id);
                            }
                            if (prev.length >= 15) return prev;
                            // 選択時は階層のデフォルト値（1階層）を設定
                            setProductGroupSummaryDepths((depths) => {
                              const newDepths = new Map(depths);
                              newDepths.set(t.id, 1);
                              return newDepths;
                            });
                            return [...prev, t.id];
                          });
                        }}
                        disabled={disabled}
                        sx={{ height: 28 }}
                      />
                    );
                  })}
                </Box>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                選択数: {selectedProductGroupTypeIds.length} / 15
              </Typography>
            </Box>

            {/* 商品グループサマリー用階層選択（タイプごと） */}
            {selectedProductGroupTypeIds.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  商品グループサマリーで使用する階層（タイプごとに指定）
                </Typography>
                <Stack spacing={2}>
                  {selectedProductGroupTypeIds.map((typeId) => {
                    const type = productGroupTypes.find((t) => t.id === typeId);
                    const currentDepth = productGroupSummaryDepths.get(typeId) ?? 1;
                    return (
                      <Box
                        key={typeId}
                        sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}
                      >
                        <FormControl>
                          <FormLabel>{type?.name ?? typeId} の階層</FormLabel>
                          <RadioGroup
                            row
                            value={currentDepth}
                            onChange={(event) => {
                              setProductGroupSummaryDepths((depths) => {
                                const newDepths = new Map(depths);
                                newDepths.set(typeId, Number(event.target.value));
                                return newDepths;
                              });
                            }}
                          >
                            {[1, 2, 3, 4, 5].map((depth) => (
                              <FormControlLabel
                                key={depth}
                                value={depth}
                                control={<Radio />}
                                label={`${depth}階層`}
                              />
                            ))}
                          </RadioGroup>
                        </FormControl>
                      </Box>
                    );
                  })}
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1 }}
                >
                  各商品グループタイプ内の階層パスを指定階層で集約してサマリーCSVを出力します
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* ファイルアップロード */}
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3 }}>
              ファイルアップロード
            </Typography>

            <FileUploadCard
              title="出荷指示書"
              required
              file={shippingOrders}
              onFileChange={(e) => handleFileUpload(e, setShippingOrders, 'shippingOrders')}
              onRemoveFile={() => handleRemoveFile(setShippingOrders)}
              description="店舗カスタマーID、店舗名、SKUカスタマーID、SKU名、出荷数量を含むCSVファイル"
              warningText="店舗カスタマーIDを倉庫カスタマーIDとして取り扱います。旧バージョンでも同じファイルを使用するため、列名は変更していません。"
              previousFileName={previousExecution?.shipping_orders_file_name}
              usePrevious={usePreviousFiles.shippingOrders}
              onTogglePrevious={() => togglePreviousFileUsage('shippingOrders')}
            />

            <FileUploadCard
              title="集計対象の店舗"
              required
              file={targetSellers}
              onFileChange={(e) => handleFileUpload(e, setTargetSellers, 'targetSellers')}
              onRemoveFile={() => handleRemoveFile(setTargetSellers)}
              description="店舗カスタマーID、店舗名、倉庫カスタマーID、倉庫名、EC店舗フラグを含むCSVファイル"
              previousFileName={previousExecution?.target_sellers_file_name}
              usePrevious={usePreviousFiles.targetSellers}
              onTogglePrevious={() => togglePreviousFileUsage('targetSellers')}
            />

            <FileUploadCard
              title="出荷元倉庫マッピング"
              required={true}
              file={sourceWarehouseMapping}
              onFileChange={(e) =>
                handleFileUpload(e, setSourceWarehouseMapping, 'sourceWarehouseMapping')
              }
              onRemoveFile={() => handleRemoveFile(setSourceWarehouseMapping)}
              description="出荷元倉庫カスタマーID、出荷元倉庫名 → 出荷先倉庫カスタマーID、出荷先倉庫名を含むCSVファイル"
              previousFileName={previousExecution?.store_warehouse_mapping_file_name}
              usePrevious={usePreviousFiles.sourceWarehouseMapping}
              onTogglePrevious={() => togglePreviousFileUsage('sourceWarehouseMapping')}
            />

            <FileUploadCard
              title="集計対象外のSKU"
              required={false}
              file={excludedSkus}
              onFileChange={(e) => handleFileUpload(e, setExcludedSkus, 'excludedSkus')}
              onRemoveFile={() => handleRemoveFile(setExcludedSkus)}
              description="集計から除外するSKUカスタマーID、SKU名を含むCSVファイル"
              previousFileName={previousExecution?.excluded_skus_file_name}
              usePrevious={usePreviousFiles.excludedSkus}
              onTogglePrevious={() => togglePreviousFileUsage('excludedSkus')}
            />

            <FileUploadCard
              title="集計対象外の店舗×SKU"
              required={false}
              file={excludedSellerSkuCombinations}
              onFileChange={(e) =>
                handleFileUpload(
                  e,
                  setExcludedSellerSkuCombinations,
                  'excludedSellerSkuCombinations',
                )
              }
              onRemoveFile={() => handleRemoveFile(setExcludedSellerSkuCombinations)}
              description="集計から除外する店舗カスタマーID、店舗名、SKUカスタマーID、SKU名を含むCSVファイル"
              previousFileName={previousExecution?.excluded_seller_sku_combinations_file_name}
              usePrevious={usePreviousFiles.excludedSellerSkuCombinations}
              onTogglePrevious={() => togglePreviousFileUsage('excludedSellerSkuCombinations')}
            />

            <FileUploadCard
              title="商品グループフィルタ"
              required={false}
              file={productGroupFilter}
              onFileChange={(e) => handleFileUpload(e, setProductGroupFilter, 'productGroupFilter')}
              onRemoveFile={() => handleRemoveFile(setProductGroupFilter)}
              description="タイプカスタマーID、1階層カスタマーID〜5階層カスタマーIDを含むCSVファイル。倉庫出荷効果データの集計対象SKUを商品グループで絞り込みます。"
              previousFileName={previousExecution?.product_group_filter_file_name}
              usePrevious={usePreviousFiles.productGroupFilter}
              onTogglePrevious={() => togglePreviousFileUsage('productGroupFilter')}
            />

            {/* 新商品除外セクション */}
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle1">新商品除外</Typography>
                  <Chip label="任意" size="small" variant="outlined" color="default" />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  指定した店舗グループまたは店舗における新商品をSKUレベルで除外します。
                </Typography>

                {/* 新商品判定基準日 */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body1" component="label">
                      新商品判定基準日
                    </Typography>
                    <Chip label="任意" size="small" variant="outlined" color="default" />
                  </Box>
                  <TextField
                    type="date"
                    value={newProductBaseDate}
                    onChange={(e) => setNewProductBaseDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    helperText="この日付を基準に新商品判定を行います（ファイルアップロード時は必須）"
                  />
                </Box>

                {/* 除外タイプ選択 */}
                <FormControl component="fieldset" sx={{ mb: 3 }}>
                  <FormLabel component="legend">除外対象の種別</FormLabel>
                  <RadioGroup
                    value={newProductExclusionType}
                    onChange={(e) =>
                      setNewProductExclusionType(e.target.value as 'store_group' | 'store')
                    }
                    row
                  >
                    <FormControlLabel
                      value="store_group"
                      control={<Radio />}
                      label="店舗グループ"
                    />
                    <FormControlLabel value="store" control={<Radio />} label="店舗" />
                  </RadioGroup>
                </FormControl>

                {/* ファイルアップロード */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2">
                    {newProductExclusionType === 'store_group'
                      ? '店舗グループ一覧CSV'
                      : '店舗一覧CSV'}
                  </Typography>
                  {(newProductExclusion || usePreviousFiles.newProductExclusion) && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        if (newProductExclusion) {
                          handleRemoveFile(setNewProductExclusion);
                        } else if (usePreviousFiles.newProductExclusion) {
                          togglePreviousFileUsage('newProductExclusion');
                        }
                      }}
                      startIcon={<DeleteIcon size={16} />}
                    >
                      削除
                    </Button>
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {newProductExclusionType === 'store_group'
                    ? '店舗グループカスタマーID、店舗グループ名を含むCSVファイル'
                    : '店舗カスタマーID、店舗名を含むCSVファイル'}
                </Typography>

                {newProductExclusion ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                    }}
                  >
                    <FileIcon size={16} style={{ marginRight: 8 }} />
                    <Typography variant="body2">{newProductExclusion.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({(newProductExclusion.size / 1024).toFixed(1)} KB)
                    </Typography>
                  </Box>
                ) : usePreviousFiles.newProductExclusion &&
                  previousExecution?.new_product_exclusion_file_name ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                      bgcolor: 'success.light',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'success.main',
                    }}
                  >
                    <FileIcon size={16} style={{ marginRight: 8 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        color="success.dark"
                        sx={{ fontWeight: 'medium' }}
                      >
                        {previousExecution.new_product_exclusion_file_name}
                      </Typography>
                      <Typography variant="caption" color="success.dark">
                        前回実行時のファイル
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon size={16} />}
                    fullWidth
                  >
                    CSVファイルを選択
                    <input
                      type="file"
                      accept=".csv"
                      hidden
                      onChange={(e) =>
                        handleFileUpload(e, setNewProductExclusion, 'newProductExclusion')
                      }
                    />
                  </Button>
                )}

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  ※ このファイルを使用する場合は、新商品判定基準日の設定が必須です
                </Typography>
              </CardContent>
            </Card>

            <Button
              variant="contained"
              size="large"
              onClick={handleAnalyze}
              disabled={isAnalyzing}
              startIcon={
                isAnalyzing ? <CircularProgress size={16} /> : <TrendingUpIcon size={16} />
              }
              fullWidth
              sx={{ mt: 3 }}
            >
              {isAnalyzing ? '分析実行中...' : '分析実行'}
            </Button>

            {/* エラーメッセージとサクセスメッセージを分析実行ボタンの下に表示 */}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
